import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { 
  Search, Shield, Loader2, Wifi, Lock, 
  Network, Key, Server, HelpCircle, ChevronRight
} from 'lucide-react';
import { API } from '../App';
import { toast } from 'sonner';

const Navigation = () => {
  const navigate = useNavigate();

  return (
    <nav className="nav-glass fixed top-0 left-0 right-0 z-50" data-testid="search-navigation">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="w-10 h-10 bg-[#00F0FF] flex items-center justify-center">
            <Shield className="w-6 h-6 text-black" />
          </div>
          <span className="font-bold text-xl tracking-tight">Digital Associates</span>
        </Link>
        
        <div className="hidden md:flex items-center gap-8">
          <Link to="/search" className="text-white transition-colors" data-testid="nav-search">
            AI Search
          </Link>
          <Link to="/services" className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-services">
            Services
          </Link>
          <Link to="/vdi" className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-vdi">
            Remote Access
          </Link>
          <Link to="/contact" className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-contact">
            Contact
          </Link>
        </div>

        <Link to="/contact">
          <Button className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 btn-glow font-medium">
            Get in Touch
          </Button>
        </Link>
      </div>
    </nav>
  );
};

const suggestedQueries = [
  { icon: Wifi, text: "How to troubleshoot WiFi connectivity issues?" },
  { icon: Lock, text: "Best practices for password management in enterprise" },
  { icon: Network, text: "Steps to configure a secure VPN connection" },
  { icon: Key, text: "How to set up multi-factor authentication?" },
  { icon: Server, text: "Firewall configuration for small business" },
  { icon: HelpCircle, text: "Microsoft 365 admin center basics" },
];

export default function SearchPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState(location.state?.initialQuery || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const textareaRef = useRef(null);

  useEffect(() => {
    // Auto-search if there's an initial query
    if (location.state?.initialQuery) {
      handleSearch(location.state.initialQuery);
    }
  }, []);

  const handleSearch = async (searchQuery = query) => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${API}/search/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery, include_docs: false })
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      setResult(data);
      setSearchHistory(prev => [{ query: searchQuery, timestamp: new Date() }, ...prev.slice(0, 4)]);
    } catch (error) {
      toast.error('Search service temporarily unavailable. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSearch();
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    handleSearch(suggestion);
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="search-page">
      <Navigation />
      
      <div className="pt-24 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Enterprise IT <span className="text-[#00F0FF]">Search</span>
            </h1>
            <p className="text-[#A1A1AA] text-lg">
              Get instant, AI-powered answers to your IT questions
            </p>
          </motion.div>

          {/* Search Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="terminal-card mb-8"
          >
            <div className="terminal-header">
              <div className="terminal-dot red" />
              <div className="terminal-dot yellow" />
              <div className="terminal-dot green" />
              <span className="text-sm text-[#A1A1AA] ml-2 font-mono">it-search</span>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              <div className="flex items-start gap-3">
                <span className="text-[#00FF94] font-mono mt-3">$</span>
                <Textarea
                  ref={textareaRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ask any IT-related question..."
                  className="search-input flex-1 min-h-[80px] resize-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                  data-testid="search-input"
                />
                <Button 
                  type="submit"
                  disabled={loading || !query.trim()}
                  className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 btn-glow px-6 py-6"
                  data-testid="search-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Search className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </form>
          </motion.div>

          {/* Loading State */}
          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="terminal-card p-6 mb-8"
            >
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-[#00F0FF] animate-spin" />
                <span className="font-mono text-[#A1A1AA]">Analyzing your query...</span>
              </div>
            </motion.div>
          )}

          {/* Result */}
          {result && !loading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="terminal-card mb-8"
              data-testid="search-result"
            >
              <div className="terminal-header">
                <div className="terminal-dot green" />
                <span className="text-sm text-[#00FF94] ml-2 font-mono">response</span>
              </div>
              <div className="p-6">
                <div className="prose prose-invert max-w-none">
                  <div className="text-white leading-relaxed whitespace-pre-wrap">
                    {result.answer}
                  </div>
                </div>
                {result.sources && result.sources.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-[#27272A]">
                    <p className="text-sm text-[#A1A1AA] mb-2">Sources from your documents:</p>
                    <div className="flex flex-wrap gap-2">
                      {result.sources.map((source, index) => (
                        <span 
                          key={index}
                          className="text-xs px-2 py-1 bg-[#151515] border border-[#27272A] text-[#00F0FF] font-mono"
                        >
                          {source}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Suggested Queries */}
          {!result && !loading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="text-lg font-bold mb-4 text-[#A1A1AA]">Suggested Questions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {suggestedQueries.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(item.text)}
                    className="service-card p-4 text-left flex items-center gap-3 group"
                    data-testid={`suggestion-${index}`}
                  >
                    <item.icon className="w-5 h-5 text-[#00F0FF] flex-shrink-0" />
                    <span className="text-sm text-[#A1A1AA] group-hover:text-white transition-colors">
                      {item.text}
                    </span>
                    <ChevronRight className="w-4 h-4 ml-auto text-[#27272A] group-hover:text-[#00F0FF] transition-colors" />
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Search History */}
          {searchHistory.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-12"
            >
              <h3 className="text-lg font-bold mb-4 text-[#A1A1AA]">Recent Searches</h3>
              <div className="space-y-2">
                {searchHistory.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(item.query)}
                    className="w-full text-left p-3 bg-[#0A0A0A] border border-[#27272A] hover:border-[#00F0FF] transition-colors flex items-center gap-3"
                  >
                    <Search className="w-4 h-4 text-[#A1A1AA]" />
                    <span className="text-sm text-[#A1A1AA] truncate">{item.query}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Need Help */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-12 mb-12 text-center"
          >
            <p className="text-[#A1A1AA] mb-4">
              Can't find what you're looking for? Our team is here to help.
            </p>
            <div className="flex justify-center gap-4">
              <Link to="/contact">
                <Button variant="outline" className="border-[#27272A]">
                  Contact Support
                </Button>
              </Link>
              <Button 
                variant="outline" 
                className="border-[#27272A]"
                onClick={() => window.location.href = 'tel:02035820582'}
              >
                Call 0203 582 0582
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
